// 2FA management — TOTP (authenticator app) + passkeys (WebAuthn).
// Lives inside Settings → Security. Talks directly to /api/v1/auth/2fa/*.
//
// TOTP enrollment is a 3-step wizard: start (server returns secret + otpauth
// URI) → user scans QR + types code → verify (server returns recovery codes
// once). The user is responsible for stashing the recovery codes — we surface
// a copy-to-clipboard control with a clear "shown once" warning.

import { useEffect, useRef, useState } from "react";
import { Button } from "../../lib/brand";
import { Modal, TextField, useToast } from "../../lib/ui";
import { env } from "../../config/env";
import { useAuth } from "../../lib/auth/AuthContext";

interface TwoFactorStatus {
  totpEnabled: boolean;
  passkeyCount: number;
}

interface PasskeyRow {
  credentialId: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}

const card: React.CSSProperties = {
  background: "var(--bg-elev)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: 24,
};
const mono: React.CSSProperties = { fontFamily: "var(--font-mono)" };

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  // voc_access cookie rides along via credentials:"include".
  return fetch(`${env.apiBaseUrl.replace(/\/web$/, "")}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
}

export function TwoFactorPanel() {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    if (authStatus !== "authenticated") return;
    try {
      const res = await authedFetch("/auth/2fa/status");
      if (!res.ok) throw new Error("status load failed");
      setStatus((await res.json()) as TwoFactorStatus);
    } catch (e) {
      console.warn("2fa status load failed", e);
    }
  }

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [authStatus]);

  if (authStatus !== "authenticated") return null;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <TotpCard status={status} reload={reload} busy={busy} setBusy={setBusy} />
      <PasskeyCard reload={reload} />
    </div>
  );
}

// ----------------------------------------------------------------- TOTP

function TotpCard({ status, reload, busy, setBusy }: {
  status: TwoFactorStatus | null; reload: () => Promise<void>;
  busy: boolean; setBusy: (v: boolean) => void;
}) {
  const { notify } = useToast();
  const [wizard, setWizard] = useState<null | { secret: string; uri: string }>(null);
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  async function startEnroll() {
    setBusy(true);
    try {
      const res = await authedFetch("/auth/2fa/totp/enroll/start", { method: "POST" });
      if (!res.ok) throw new Error("enroll start failed");
      const body = await res.json();
      setWizard({ secret: body.secret, uri: body.provisioningUri });
    } catch (e) {
      notify({ state: "error", message: e instanceof Error ? e.message : "Enroll failed." });
    } finally { setBusy(false); }
  }

  async function completeEnroll() {
    if (!wizard) return;
    setBusy(true);
    try {
      const res = await authedFetch("/auth/2fa/totp/enroll/complete", {
        method: "POST", body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? "Code didn't verify.");
      }
      const body = await res.json();
      setRecoveryCodes(body.recoveryCodes);
      setWizard(null);
      setCode("");
      notify({ state: "complete", message: "2FA enabled. Save your recovery codes." });
      reload();
    } catch (e) {
      notify({ state: "error", message: e instanceof Error ? e.message : "Failed." });
    } finally { setBusy(false); }
  }

  async function disable() {
    setBusy(true);
    try {
      const res = await authedFetch("/auth/2fa/totp/disable", {
        method: "POST", body: JSON.stringify({ code: disableCode }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? "Failed.");
      }
      notify({ state: "complete", message: "2FA disabled." });
      setConfirmingDisable(false);
      setDisableCode("");
      reload();
    } catch (e) {
      notify({ state: "error", message: e instanceof Error ? e.message : "Failed." });
    } finally { setBusy(false); }
  }

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>Authenticator app (TOTP)</h2>
          <p style={{ fontSize: 13, color: "var(--fg-muted)", marginTop: 4 }}>
            Scan a QR code with 1Password, Google Authenticator, Authy, or any TOTP app. Sign-in then asks for a 6-digit code.
          </p>
        </div>
        <span style={{
          fontSize: 11, fontFamily: "var(--font-mono)", textTransform: "uppercase", letterSpacing: "0.08em",
          padding: "3px 8px", borderRadius: 4, border: "1px solid var(--border)",
          color: status?.totpEnabled ? "#22c55e" : "var(--fg-muted)",
        }}>{status?.totpEnabled ? "ENABLED" : "OFF"}</span>
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
        {!status?.totpEnabled && (
          <Button variant="primary" size="sm" disabled={busy} onClick={startEnroll}>Enable</Button>
        )}
        {status?.totpEnabled && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmingDisable(true)}>Disable</Button>
        )}
      </div>

      {wizard && (
        <Modal open onClose={() => setWizard(null)} title="Set up your authenticator">
          <div style={{ display: "grid", gap: 16 }}>
            <p style={{ fontSize: 13, color: "var(--fg-muted)" }}>
              Scan the QR or paste the secret into your authenticator app, then enter the 6-digit code it shows.
            </p>
            <QrCode value={wizard.uri} />
            <div style={{ ...mono, fontSize: 11, color: "var(--fg-muted)", wordBreak: "break-all", textAlign: "center" }}>
              {wizard.secret}
            </div>
            <TextField
              label="Code from your authenticator"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
              autoComplete="one-time-code"
            />
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <Button variant="ghost" size="sm" onClick={() => setWizard(null)} disabled={busy}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={busy || code.length !== 6} onClick={completeEnroll}>Verify + enable</Button>
            </div>
          </div>
        </Modal>
      )}

      {recoveryCodes && (
        <Modal open onClose={() => setRecoveryCodes(null)} title="Save your recovery codes">
          <p style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 12 }}>
            These codes get you back in if you lose your authenticator. <strong>Each works once.</strong> We'll never show them again.
          </p>
          <div style={{ ...mono, fontSize: 13, padding: 12, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, display: "grid", gap: 4 }}>
            {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 14 }}>
            <Button variant="ghost" size="sm" onClick={() => {
              navigator.clipboard?.writeText(recoveryCodes.join("\n"));
              notify({ state: "complete", message: "Copied." });
            }}>Copy</Button>
            <Button variant="primary" size="sm" onClick={() => setRecoveryCodes(null)}>I've saved them</Button>
          </div>
        </Modal>
      )}

      {confirmingDisable && (
        <Modal open onClose={() => setConfirmingDisable(false)} title="Disable two-factor authentication?">
          <p style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 12 }}>
            Enter a current code to confirm.
          </p>
          <TextField
            label="Current 6-digit code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
            autoComplete="one-time-code"
          />
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 14 }}>
            <Button variant="ghost" size="sm" onClick={() => setConfirmingDisable(false)} disabled={busy}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={busy || disableCode.length !== 6} onClick={disable}>Disable 2FA</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- Passkey

function PasskeyCard({ reload }: { reload: () => Promise<void> }) {
  const { notify } = useToast();
  const [list, setList] = useState<PasskeyRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");
  const [adding, setAdding] = useState(false);

  async function loadList() {
    try {
      const res = await authedFetch("/auth/2fa/passkey/list");
      if (!res.ok) return;
      setList((await res.json()) as PasskeyRow[]);
    } catch { /* ignore */ }
  }

  useEffect(() => { loadList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function enroll() {
    if (!window.PublicKeyCredential) {
      notify({ state: "error", message: "This browser doesn't support passkeys." });
      return;
    }
    setBusy(true);
    try {
      const startRes = await authedFetch("/auth/2fa/passkey/register/start", { method: "POST" });
      if (!startRes.ok) throw new Error("Couldn't start enrolment.");
      const { options, challengeToken } = await startRes.json();
      const cred = await navigator.credentials.create({ publicKey: decodeRegisterOptions(options) });
      if (!cred) throw new Error("No credential returned.");
      const payload = encodeCredentialForRegister(cred as PublicKeyCredential);
      const completeRes = await authedFetch("/auth/2fa/passkey/register/complete", {
        method: "POST",
        body: JSON.stringify({ challengeToken, credential: payload, label: labelDraft || "Passkey" }),
      });
      if (!completeRes.ok) {
        const body = await completeRes.json().catch(() => ({}));
        throw new Error(body.detail ?? "Passkey verification failed.");
      }
      notify({ state: "complete", message: "Passkey enrolled." });
      setLabelDraft("");
      setAdding(false);
      loadList();
      reload();
    } catch (e) {
      notify({ state: "error", message: e instanceof Error ? e.message : "Failed." });
    } finally { setBusy(false); }
  }

  async function removeKey(credentialId: string) {
    if (!confirm("Remove this passkey?")) return;
    setBusy(true);
    try {
      const res = await authedFetch(`/auth/2fa/passkey/${encodeURIComponent(credentialId)}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Remove failed");
      notify({ state: "complete", message: "Passkey removed." });
      loadList();
      reload();
    } catch (e) {
      notify({ state: "error", message: e instanceof Error ? e.message : "Failed." });
    } finally { setBusy(false); }
  }

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>Passkeys</h2>
          <p style={{ fontSize: 13, color: "var(--fg-muted)", marginTop: 4 }}>
            Sign in without a password using Touch ID, Face ID, Windows Hello, or a hardware security key.
          </p>
        </div>
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => setAdding(true)}>Add passkey</Button>
      </div>
      {list.length > 0 && (
        <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
          {list.map((p) => (
            <div key={p.credentialId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--bg)" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: "var(--fg)" }}>{p.label}</div>
                <div style={{ fontSize: 11, color: "var(--fg-muted)", ...mono }}>
                  Added {new Date(p.createdAt).toLocaleDateString()} · Last used {p.lastUsedAt ? new Date(p.lastUsedAt).toLocaleDateString() : "never"}
                </div>
              </div>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => removeKey(p.credentialId)}>Remove</Button>
            </div>
          ))}
        </div>
      )}
      {adding && (
        <Modal open onClose={() => setAdding(false)} title="Add a passkey">
          <p style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 12 }}>
            Give this passkey a name so you can identify it later (e.g. "MacBook", "iPhone", "YubiKey 5C").
          </p>
          <TextField label="Name" value={labelDraft} onChange={(e) => setLabelDraft(e.currentTarget.value)} placeholder="MacBook Touch ID" />
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 14 }}>
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)} disabled={busy}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={busy} onClick={enroll}>Use my device</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- helpers

function base64urlToBytes(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}
function bytesToBase64url(b: ArrayBuffer): string {
  const bytes = new Uint8Array(b);
  let s = "";
  for (const v of bytes) s += String.fromCharCode(v);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeRegisterOptions(opts: any): PublicKeyCredentialCreationOptions {
  return {
    ...opts,
    challenge: base64urlToBytes(opts.challenge),
    user: { ...opts.user, id: base64urlToBytes(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials ?? []).map((c: any) => ({ ...c, id: base64urlToBytes(c.id) })),
  };
}

export function decodeLoginOptions(opts: any): PublicKeyCredentialRequestOptions {
  return {
    ...opts,
    challenge: base64urlToBytes(opts.challenge),
    allowCredentials: (opts.allowCredentials ?? []).map((c: any) => ({ ...c, id: base64urlToBytes(c.id) })),
  };
}

function encodeCredentialForRegister(cred: PublicKeyCredential): any {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bytesToBase64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bytesToBase64url(r.clientDataJSON),
      attestationObject: bytesToBase64url(r.attestationObject),
      transports: (r as any).getTransports?.() ?? [],
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

export function encodeCredentialForLogin(cred: PublicKeyCredential): any {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bytesToBase64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bytesToBase64url(r.clientDataJSON),
      authenticatorData: bytesToBase64url(r.authenticatorData),
      signature: bytesToBase64url(r.signature),
      userHandle: r.userHandle ? bytesToBase64url(r.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

// Inline QR generator using a tiny stdlib-only renderer so we don't add a
// new npm dep. The provisioning URI is short enough that we can just hand
// it off to a public-domain renderer (kjua-style 21x21 to 33x33). For now
// we render an <img> via a chart-server-free path: each authenticator app
// also accepts the secret pasted in — the QR is convenience, not the only
// path.
function QrCode({ value }: { value: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!canvasRef.current) return;
    let cancelled = false;
    // Load the QR renderer on demand — it's only ever needed inside the
    // 2FA enrollment modal, so keep it out of the first-paint bundle.
    void import("qrcode").then(({ default: QRCode }) => {
      if (cancelled || !canvasRef.current) return;
      QRCode.toCanvas(canvasRef.current, value, { width: 220, margin: 1 }).catch(() => undefined);
    });
    return () => {
      cancelled = true;
    };
  }, [value]);
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 8 }}>
      <canvas ref={canvasRef} style={{ background: "white", borderRadius: 6 }} />
    </div>
  );
}
